// Safari throws SecurityError on any access to localStorage/sessionStorage when
// "Block All Cookies" is on (and in some in-app WebViews), and setItem can throw
// QuotaExceededError. The app touches storage in hundreds of places without
// try/catch, so guard it once here, before any other module runs.

type StorageName = "localStorage" | "sessionStorage";

function createMemoryStorage(): Storage {
  let store: Record<string, string> = {};
  return {
    get length() {
      return Object.keys(store).length;
    },
    key: (i: number) => Object.keys(store)[i] ?? null,
    getItem: (k: string) =>
      Object.prototype.hasOwnProperty.call(store, k) ? store[k] : null,
    setItem: (k: string, v: string) => {
      store[k] = String(v);
    },
    removeItem: (k: string) => {
      delete store[k];
    },
    clear: () => {
      store = {};
    },
  };
}

function isUsable(name: StorageName): boolean {
  try {
    const s = window[name];
    const probe = "__storage_probe__";
    s.setItem(probe, probe);
    s.removeItem(probe);
    return true;
  } catch (e) {
    return false;
  }
}

export function installSafeStorage() {
  (["localStorage", "sessionStorage"] as StorageName[]).forEach((name) => {
    if (isUsable(name)) return;
    try {
      Object.defineProperty(window, name, {
        value: createMemoryStorage(),
        configurable: true,
      });
      console.warn(name + " unavailable, using in-memory fallback");
    } catch (e) {
      console.error("Could not install " + name + " fallback", e);
    }
  });

  try {
    const originalSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function (key: string, value: string) {
      try {
        originalSetItem.call(this, key, value);
      } catch (e) {
        console.warn("storage setItem failed for " + key, e);
      }
    };
  } catch (e) {
    // Storage may not exist in very restricted environments
  }
}

installSafeStorage();
